"use client";

import { ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="services-hero" id="services-hero" className="relative overflow-hidden bg-background text-foreground pt-32 md:pt-40 pb-20 md:pb-28 border-b border-border">
      {/* Background blur blobs */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-20 -left-16 h-72 w-72 md:h-96 md:w-96" style={{
      background: "var(--primary)",
      opacity: 0.08,
      borderRadius: "62% 38% 47% 53% / 45% 60% 40% 55%"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/3 right-1/4 h-60 w-60 hidden md:block" style={{
      background: "var(--primary)",
      opacity: 0.05,
      borderRadius: "58% 42% 63% 37% / 50% 45% 55% 50%"
    }} />

      <div className="container mx-auto max-w-[1320px] px-6 md:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Content Column */}
          <div ref={contentRef} className="lg:col-span-7 transition-all duration-700 ease-out" style={{
          transform: contentVisible ? "translateY(0)" : "translateY(24px)",
          opacity: contentVisible ? 1 : 0
        }}>
            <div className="flex items-center gap-3 mb-4">
              <span aria-hidden="true" className="relative inline-flex">
                <span className="block h-1.5 w-1.5 rounded-md" style={{
                background: "var(--primary)"
              }} />
              </span>
              <span className="font-mono text-xs md:text-sm font-medium uppercase text-muted-foreground" style={{
              letterSpacing: "0.16em"
            }}>
                Comprehensive Pest Solutions
              </span>
            </div>

            <span className="font-mono text-xs md:text-sm uppercase tracking-[0.15em] text-primary mb-3 block">
              Protecting Homes. Protecting Families. It&apos;s Our Promise.
            </span>

            <h1 className="font-default font-bold text-foreground" style={{
            fontSize: "clamp(2.5rem, 5.5vw, 5rem)",
            letterSpacing: "-0.025em",
            lineHeight: "1.05"
          }}>
              Pest Control Services in Durham &amp; the Triangle, NC
            </h1>

            <p className="mt-6 font-default font-normal max-w-[56ch]" style={{
            color: "var(--muted-foreground)",
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            lineHeight: "1.55"
          }}>
              Attic to Basement Pest Control is a family-owned, licensed and insured company based in Durham, NC. From general pest control to mosquito and rodent protection, all services start with a free inspection.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:gap-5">
              <Button asChild className="group rounded-md h-auto px-7 py-3.5 text-base font-bold font-default bg-primary text-primary-foreground border-[1.5px] hover:bg-primary/90 transition-all duration-200 hover:translate-x-0.5 shadow-md" style={{
              borderColor: "var(--secondary)"
            }}>
                <Link to="/ContactPage">
                  Get Your Free Inspection
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" className="rounded-md h-auto px-7 py-3.5 text-base font-bold font-default bg-transparent border-[1.5px] text-foreground hover:bg-secondary hover:text-secondary-foreground transition-colors duration-200 shadow-sm" style={{
              borderColor: "var(--secondary)"
            }}>
                <Link href="tel:9198961953">
                  <Phone className="mr-2 h-4 w-4 text-primary" />
                  Call 919-896-1953
                </Link>
              </Button>
            </div>
          </div>

          {/* Image Column */}
          <div ref={imageRef} className="lg:col-span-5 relative transition-all duration-700 ease-out" style={{
          transform: imageVisible ? "translateY(0)" : "translateY(24px)",
          opacity: imageVisible ? 1 : 0,
          transitionDelay: "150ms"
        }}>
            <div className="relative mx-auto max-w-[480px] lg:max-w-none">
              <div aria-hidden="true" className="pointer-events-none absolute -top-8 -left-8 h-32 w-32 md:h-40 md:w-40" style={{
              background: "var(--primary)",
              opacity: 0.2,
              borderRadius: "58% 42% 63% 37% / 50% 45% 55% 50%"
            }} />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-10 -right-6 h-36 w-36 md:h-44 md:w-44" style={{
              background: "var(--primary)",
              opacity: 0.15,
              borderRadius: "62% 38% 47% 53% / 45% 60% 40% 55%"
            }} />

              <div className="relative overflow-hidden border-[1.5px] shadow-xl" style={{
              borderRadius: "62% 38% 47% 53% / 45% 60% 40% 55%",
              borderColor: "var(--secondary)",
              aspectRatio: "1 / 1.05"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697750/img/technician_working_precision.png" alt="Attic to Basement Pest Control technician conducting an inspection" className="absolute inset-0 h-full w-full object-cover" fetchPriority="high" width={600} height={630} decoding="async" />
              </div>

              <div className="absolute top-2 right-2 md:-top-2 md:-right-4 z-10" style={{
              transform: "rotate(-6deg)"
            }}>
                <div className="bg-card px-4 py-2.5 border-[1.5px] border-dashed shadow-lg" style={{
                borderColor: "var(--secondary)",
                borderRadius: "0.5rem"
              }}>
                  <div className="flex items-center gap-2">
                    <span aria-hidden="true" className="block h-2 w-2 rounded-md" style={{
                    background: "var(--primary)"
                  }} />
                    <span className="font-mono text-xs font-medium uppercase text-foreground" style={{
                    letterSpacing: "0.12em"
                  }}>
                      Serving the Triangle
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}